import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SeopageSearch from "../containers/Index/Search/SeopageSearch";
import gatwick from "../common/images/G-1.webp";
import gatwick2 from "../common/images/G-2.webp";
import gatwick3 from "../common/images/G-3.webp";
import ParkingTable from "../containers/Gatwick/ParkingTable";
import ParkingServicesSection from "../common/Components/ParkingServicesSection/ParkingServicesSection";
import axios from "axios";
import apiUrl from "../common/config/apiUrl";
import apiKey from "../common/config/apiKey";
import Seo from "../common/Components/Seo/Seo";
import AirportProductsSchema from "../common/Components/StructuredData/AirportProductsSchema";
import { generateStructuredData } from "../common/utils/seoUtils";
import {
  FaCar, FaHandshake, FaBus, FaBuilding, FaShieldAlt, FaBolt,
  FaSave, FaStar, FaPhoneAlt, FaPlane, FaMapMarkerAlt, FaSearch,
  FaLightbulb, FaParking, FaChevronRight, FaUsers, FaChevronDown, FaChevronUp,
} from "react-icons/fa";

/* ── DATA ─────────────────────────────────────────────────── */
const PARKING_OPTIONS = [
  { id: 1, to: "/meet-and-greet-parking", icon: <FaHandshake className="text-3xl" />, title: "Meet & Greet", desc: "Drop off at terminal — we park your car. Perfect for families & business travellers.", color: "primary", badge: "Most Popular" },
  { id: 2, to: "/park-and-ride-parking", icon: <FaBus className="text-3xl" />, title: "Park & Ride", desc: "Budget-friendly parking with free shuttle transfer to your terminal.", color: "lightprimary", badge: "Best Value" },
  { id: 3, to: null, icon: <FaBuilding className="text-3xl" />, title: "Official Parking", desc: "On-site parking closest to terminal check-in desks. Secure 24/7.", color: "primary", badge: "Premium" },
];

const TERMINALS = [
  { code: "NTH", name: "North Terminal", desc: "easyJet, TUI & charter flights hub", color: "primary", to: "/gatwick-airport-parking/gatwick-north-terminal-parking" },
  { code: "STH", name: "South Terminal", desc: "British Airways, Vueling & more", color: "lightprimary", to: "/gatwick-airport-parking/gatwick-south-terminal-parking" },
];

const WHY_BOOK = [
  { icon: <FaSearch />, text: "Compare All Options" },
  { icon: <FaSave />, text: "Save Up to 70%" },
  { icon: <FaShieldAlt />, text: "24/7 Security" },
  { icon: <FaBolt />, text: "Instant Booking" },
  { icon: <FaStar />, text: "Top Rated" },
  { icon: <FaPhoneAlt />, text: "24/7 Support" },
];

const FAQS = [
  { q: "Which terminal is easyJet at Gatwick?", a: "easyJet operates from Gatwick's North Terminal. Most charter and low-cost carriers also use the North Terminal." },
  { q: "What is the cheapest parking at Gatwick?", a: "Park & Ride services offer the most budget-friendly option at Gatwick, with free shuttle transfers included." },
  { q: "How does meet & greet work at Gatwick?", a: "Drive to the terminal drop-off zone, hand your keys to a professional valet, and walk straight to check-in. Your car is returned to the same point on arrival." },
  { q: "Is Gatwick airport parking secure?", a: "Yes. All listed providers are CCTV-monitored, fully insured, and operate 24/7 security." },
  { q: "Can I book Gatwick parking online?", a: "Yes — booking online is quick, easy, and often cheaper than paying on the day." },
  { q: "Can I cancel my Gatwick parking booking?", a: "Most providers offer flexible cancellation. Always check the terms at the time of booking." },
];

/* ── COMPONENT ─────────────────────────────────────────────── */
const GatwickAirportParking = () => {
  const [airports, setAirports] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/airports?key=${apiKey}`);
        if (res?.data?.response_code == 200) setAirports(res.data.data);
      } catch (e) { console.log("airports error", e); }
    })();
  }, []);

  const seoData = {
    title: "Gatwick Airport Parking | Meet & Greet, Park & Ride & Official Parking",
    description: "Find and book Gatwick Airport parking at AirportParkingDeals.co.uk. Compare official, off-airport, meet & greet, and park & ride options for North & South Terminals.",
    keywords: "Gatwick Airport Parking, Gatwick Parking Deals, Gatwick Meet & Greet, Gatwick Park & Ride, Gatwick North Terminal Parking, Gatwick South Terminal Parking, Cheap Gatwick Parking",
    url: `${process.env.REACT_APP_SITE_URL || "https://airportparkingdeals.co.uk"}/gatwick-airport-parking`,
  };

  const structuredData = [
    generateStructuredData("organization"),
    generateStructuredData("website"),
    generateStructuredData("service", { name: "Gatwick Airport Parking Services", description: "Find and book Gatwick Airport parking." }),
  ];

  return (
    <div className="overflow-x-hidden">
      <Seo title={seoData.title} description={seoData.description} keywords={seoData.keywords} url={seoData.url} structuredData={structuredData} type="website" />
      <AirportProductsSchema airportKey="gatwick" />

      {/* ── HERO ── */}
      <SeopageSearch
        airports={airports}
        airportName="Gatwick"
        heroTitle="Stress-Free <span class='text-secondary'>Gatwick Airport</span> Parking"
        description1="Book secure and affordable parking at Gatwick — from premium Meet & Greet to convenient Park & Ride options, all at unbeatable prices."
      />

      {/* ══════════════════════════════════════════
          1. WELCOME — white bg, primary text, lightprimary accent
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-white">
        <div className="px-4 mx-auto max-w-[1280px]">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
                <FaPlane /> Gatwick Airport
              </span>
              <h1 className="mb-4 text-2xl font-bold leading-tight md:text-3xl lg:text-4xl text-primary">
                Welcome to Airport Parking <span className="text-lightprimary">Deals at Gatwick</span>
              </h1>
              <p className="mb-3 text-sm leading-relaxed md:text-base text-primary/80">
                Flying from Gatwick Airport, one of the UK's busiest airports? Finding safe, convenient, and affordable parking doesn't have to be stressful. At AirportParkingDeals.co.uk, we make it easy to compare all parking options.
              </p>
              <p className="mb-5 text-sm leading-relaxed md:text-base text-primary/80">
                Whether you're flying from the North or South Terminal, you'll find the best deals, transparent pricing, and trusted providers in one place.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: <FaShieldAlt />, text: "Fully Secured" },
                  { icon: <FaStar />, text: "Top Rated" },
                  { icon: <FaBolt />, text: "Instant Booking" },
                  { icon: <FaPhoneAlt />, text: "24/7 Support" },
                ].map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border-2 border-primary/20 bg-primary/5 text-primary">
                    <span className="text-lightprimary">{b.icon}</span>{b.text}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-secondary/20 blur-2xl -z-10" />
              <img src={gatwick} alt="Gatwick Airport Family Travel" className="object-cover w-full " />
              <div className="absolute flex items-center gap-2 px-3 py-2 text-xs font-bold text-white rounded-xl bg-primary -bottom-3 -left-3 shadow-lg">
                <FaShieldAlt className="text-secondary" /> CCTV Monitored 24/7
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          2. PARKING OPTIONS — primary/lightprimary cards, secondary badges
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-primary">
        <div className="px-4 mx-auto max-w-[1280px]">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
              <FaCar /> Parking Types
            </span>
            <h2 className="text-2xl font-bold md:text-3xl text-white">
              Gatwick <span className="text-secondary">Parking Options</span>
            </h2>
            <p className="max-w-xl mx-auto mt-2 text-sm text-white/70">
              Choose the perfect parking solution for your travel needs
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PARKING_OPTIONS.map((opt) => {
              const Card = opt.to ? Link : "div";
              const cardProps = opt.to ? { to: opt.to } : {};
              const bg = opt.color === "lightprimary" ? "bg-lightprimary" : "bg-white/10";
              return (
                <Card key={opt.id} {...cardProps}
                  className={`relative flex flex-col ${bg} text-white rounded-2xl p-6 border border-white/10 hover:border-secondary/60 hover:-translate-y-1 transition-all duration-300 overflow-hidden group`}
                >
                  <span className="absolute top-4 right-4 px-2 py-0.5 text-xs font-bold rounded-full bg-secondary text-primary">{opt.badge}</span>
                  <div className="flex items-center justify-center w-14 h-14 mb-4 rounded-xl bg-white/10">{opt.icon}</div>
                  <h3 className="mb-2 text-xl font-bold">{opt.title}</h3>
                  <p className="mb-4 text-sm opacity-80 leading-relaxed">{opt.desc}</p>
                  {opt.to && (
                    <div className="flex items-center gap-2 mt-auto text-sm font-bold text-secondary">
                      Learn More <FaChevronRight className="text-xs" />
                    </div>
                  )}
                  <div className="absolute w-28 h-28 rounded-full -bottom-8 -right-8 bg-white/5" />
                </Card>
              );
            })}
          </div>

          {/* Why Book strip */}
          <div className="p-5 mt-8 rounded-2xl bg-white/10 border border-white/10">
            <h3 className="flex items-center justify-center gap-2 mb-5 text-lg font-bold text-center text-white">
              <FaLightbulb className="text-secondary" /> Why Book With Us?
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
              {WHY_BOOK.map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <span className="text-2xl text-secondary">{item.icon}</span>
                  <p className="text-xs font-bold text-center text-white">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          3. TERMINALS — white bg, primary/lightprimary badges
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-white">
        <div className="px-4 mx-auto max-w-[1280px]">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
                <FaMapMarkerAlt /> All Terminals
              </span>
              <h2 className="mb-6 text-2xl font-bold md:text-3xl text-primary">Terminals We Cover</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {TERMINALS.map((t) => {
                  const bg = t.color === "lightprimary" ? "bg-lightprimary" : "bg-primary";
                  return (
                    <Link key={t.code} to={t.to}
                      className="flex items-start gap-3 p-4 rounded-xl border-2 border-primary/10 hover:border-lightprimary/50 hover:shadow-md transition-all bg-white shadow-sm group">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${bg} flex items-center justify-center text-white font-bold text-sm shadow`}>{t.code}</div>
                      <div className="flex-1">
                        <h3 className="mb-0.5 text-sm font-bold text-primary group-hover:text-lightprimary transition-colors">{t.name}</h3>
                        <p className="text-xs text-primary/60">{t.desc}</p>
                      </div>
                      <FaChevronRight className="text-xs text-primary/30 group-hover:text-lightprimary mt-1 flex-shrink-0 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-lightprimary/20 blur-2xl -z-10" />
              <img src={gatwick2} alt="Gatwick Terminals Overview" className="object-cover w-full " />
              <div className="absolute flex items-center gap-2 px-3 py-2 text-xs font-bold text-white rounded-xl bg-lightprimary -bottom-3 -right-3 shadow-lg">
                <FaMapMarkerAlt className="text-secondary" /> 2 Terminals Covered
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          4. PRICING TABLE — lightprimary bg
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-lightprimary/10">
        <div className="px-4 mx-auto max-w-[1280px]">
          <div className="mb-8 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
              <FaParking /> Live Prices
            </span>
            <h2 className="text-2xl font-bold md:text-3xl text-primary">
              Compare <span className="text-lightprimary">Gatwick</span> Parking Prices
            </h2>
            <p className="max-w-xl mx-auto mt-2 text-sm text-primary/70">Top Gatwick Airport parking providers</p>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-lg">
            <ParkingTable />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          5. WHY BOOK — primary bg, secondary accents
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-primary">
        <div className="px-4 mx-auto max-w-[1280px]">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
                Why Choose Us
              </span>
              <h2 className="mb-6 text-2xl font-bold md:text-3xl text-white">
                Why Book with Airport <span className="text-secondary">ParkingDeals.co.uk?</span>
              </h2>
              <div className="flex flex-col gap-3">
                {[
                  "Compare North & South Terminal parking in one place",
                  <span>See <strong className="text-secondary">transparent pricing</strong> – no hidden fees</span>,
                  <span>Choose from <strong className="text-secondary">budget</strong>, mid-range, or premium services</span>,
                  <span>Read <strong className="text-secondary">real customer reviews</strong> before booking</span>,
                  <span>Book online in minutes with <strong className="text-secondary">instant confirmation</strong></span>,
                ].map((pt, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/15 transition-colors">
                    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-secondary text-primary font-bold text-sm">{i + 1}</div>
                    <p className="text-sm text-white">{pt}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-secondary/10 blur-2xl -z-10" />
              <img src={gatwick3} alt="Professional Parking Service" className="w-full rounded-2xl shadow-xl" />
              <div className="absolute flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-xl bg-secondary text-primary -bottom-3 -right-3 shadow-lg">
                <FaUsers /> Trusted by 1000s
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          6. FAQs — white bg, lightprimary accents
      ══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-white">
        <div className="px-4 mx-auto max-w-[1280px]">

          {/* CTA Box */}
          <div className="relative p-6 mb-10 overflow-hidden rounded-2xl bg-lightprimary text-white shadow-xl">
            <div className="absolute w-40 h-40 rounded-full -top-10 -right-10 bg-white/10 pointer-events-none" />
            <div className="absolute w-28 h-28 rounded-full -bottom-8 -left-8 bg-white/10 pointer-events-none" />
            <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-bold uppercase rounded-full bg-secondary text-primary">
                  Final Takeaway
                </span>
                <h2 className="mb-2 text-xl font-bold md:text-2xl">Ready to Park at Gatwick?</h2>
                <p className="text-sm opacity-90 md:text-base max-w-lg">
                  Whether you're flying from the North or South Terminal, AirportParkingDeals.co.uk makes it simple to find, compare, and book the best parking deal.
                </p>
              </div>
              <button className="flex-shrink-0 flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-xl bg-secondary text-primary hover:bg-white transition-colors shadow-lg">
                <FaPlane className="-rotate-45" /> Book Gatwick Parking Today
              </button>
            </div>
          </div>

          {/* FAQ heading */}
          <div className="mb-6 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-bold uppercase rounded-full bg-secondary text-primary">FAQs</span>
            <h2 className="text-2xl font-bold md:text-3xl text-primary">
              Gatwick <span className="text-lightprimary">Airport Parking</span> FAQs
            </h2>
            <p className="max-w-xl mx-auto mt-2 text-sm text-primary/70">Everything you need to know before booking</p>
          </div>

          {/* FAQ grid */}
          <div className="grid gap-3 sm:grid-cols-2">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl border-2 overflow-hidden transition-all ${openFaq === i ? "border-lightprimary shadow-md" : "border-primary/10 hover:border-lightprimary/40"}`}>
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-left gap-3"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="flex items-center gap-2 flex-1">
                    <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-secondary text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm font-bold text-primary">{faq.q}</span>
                  </span>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${openFaq === i ? "bg-lightprimary text-white" : "bg-primary/10 text-primary"}`}>
                    {openFaq === i ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 pt-1 border-t border-primary/10">
                    <p className="text-sm text-primary/80 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ParkingServicesSection />
    </div>
  );
};

export default GatwickAirportParking;
